import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Pagination from '../../../../components/atoms/Pagination';
import { dashboardTokens } from '../../../Dashboard/Components/dashboardCardStyles';
import { campaignStatusMeta, formatCurrency } from '../../components/leadsMeta';
import { Campaign, CampaignPerformanceRow } from '../../../../services/marketingLeads';

const headCellSx = {
  color: dashboardTokens.textSecondary,
  borderColor: dashboardTokens.border,
  whiteSpace: 'nowrap' as const,
  fontSize: 13,
  fontWeight: 500,
};

const bodyCellSx = {
  borderColor: dashboardTokens.border,
  whiteSpace: 'nowrap' as const,
  fontSize: 13.5,
};

interface MetaCampaignsTableProps {
  // One row per campaign from GET /admin/leads/campaigns/performance:
  // lead counts from the lead rows, spend from the insights sync.
  rows: CampaignPerformanceRow[];
  campaigns: Campaign[];
  loading: boolean;
  platformLabel?: string;
  /** ISO 4217 of the connected ad account — the money columns are in it. */
  currency?: string;
}

export const MetaCampaignsTable: React.FC<MetaCampaignsTableProps> = ({
  rows,
  campaigns,
  loading,
  platformLabel = 'Meta Ads',
  currency = 'GBP',
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Status fallback for rows the performance endpoint marks UNKNOWN (leads
  // whose campaign was deleted) — try the catalog by name before giving up.
  const statusByName = useMemo(() => {
    const map = new Map<string, string>();
    campaigns.forEach((c) => map.set(c.name, c.status));
    return map;
  }, [campaigns]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((a) => a.campaignName.toLowerCase().includes(term));
  }, [rows, search]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box
      sx={{
        bgcolor: dashboardTokens.cardBg,
        border: `1px solid ${dashboardTokens.border}`,
        borderRadius: dashboardTokens.cardRadius,
        p: 2.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>
            All {platformLabel} Campaigns
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary, mt: 0.25 }}>
            Leads received, spend and cost per lead for the selected date range
          </Typography>
        </Box>

        <TextField
          size="small"
          placeholder="Search campaign..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 240,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#1c1c1c',
              color: '#e2e8f0',
              fontSize: 13.5,
              '& fieldset': { borderColor: dashboardTokens.border },
            },
          }}
        />
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Campaign</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={headCellSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Leads
                  <Tooltip title="Leads received via the webhook in this range. The number in brackets is what the ad platform itself reports for the same days — a gap means submissions were missed (Page not subscribed, server down) or attributed to other days.">
                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell sx={headCellSx}>Qualified</TableCell>
              <TableCell sx={headCellSx}>Converted</TableCell>
              <TableCell sx={headCellSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Cost per Lead
                  <Tooltip title="Amount spent ÷ leads received, per campaign. Needs the nightly campaign sync (Sync now on the Leads page).">
                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell sx={headCellSx}>Amount Spent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ ...bodyCellSx, color: '#94a3b8' }}>
                  Loading campaigns…
                </TableCell>
              </TableRow>
            ) : pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ ...bodyCellSx, color: '#94a3b8' }}>
                  {search
                    ? 'No campaigns match that search.'
                    : `No ${platformLabel} campaign activity in this date range yet.`}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => {
                const status =
                  row.status !== 'UNKNOWN' ? row.status : statusByName.get(row.campaignName) || 'ACTIVE';
                const meta = campaignStatusMeta[status] || campaignStatusMeta.ACTIVE;
                return (
                  <TableRow key={row.campaignId || row.campaignName} hover>
                    <TableCell sx={{ ...bodyCellSx, color: '#e2e8f0', fontWeight: 600 }}>
                      {row.campaignName}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip
                        label={meta.label}
                        size="small"
                        sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700, height: 22 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: '#e2e8f0' }}>
                      {row.leads}
                      {row.reportedLeads !== row.leads && (
                        <Box component="span" sx={{ color: row.reportedLeads > row.leads ? '#f59e0b' : dashboardTokens.textMuted, ml: 0.5, fontSize: 12 }}>
                          ({row.reportedLeads} reported)
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: '#e2e8f0' }}>{row.qualified}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: '#e2e8f0' }}>{row.converted}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: row.costPerLead == null ? dashboardTokens.textMuted : '#e2e8f0' }}>
                      {row.costPerLead == null ? '—' : formatCurrency(row.costPerLead, currency)}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: row.amountSpent ? '#e2e8f0' : dashboardTokens.textMuted }}>
                      {row.amountSpent ? formatCurrency(row.amountSpent, currency) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      {filtered.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Pagination
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_: unknown, newPage: number) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      )}
    </Box>
  );
};
